import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid, Card, IconButton, Tooltip, Typography, Tabs, Tab, Box, TextField } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MapContainer, Popup, TileLayer, useMap, CircleMarker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PageLoader from "components/PageLoader/PageLoader";
import AppSnackbar from "components/AppSnackbar/AppSnackbar";
import { fetchLivePartners } from "./services/services";
import { socket, connectSocket, disconnectSocket } from "components/socket/socket";

const defaultCenter = { lat: 25.0, lng: 66.0 };
const PARTNERS_CACHE_KEY = "cdone_live_partners_cache";

const circleStyles = (color) => ({
  color,
  fillColor: color,
  fillOpacity: 0.85,
  weight: 3,
});
const MAX_PATH_POINTS = 60;
const toTimestamp = (value) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

function MapAutoFit({ partners, autoFitEnabled, onUserInteraction }) {
  const map = useMap();
  useMapEvents({
    zoomstart: onUserInteraction,
    dragstart: onUserInteraction,
  });

  useEffect(() => {
    if (!map || !autoFitEnabled) return;

    if (!partners.length) {
      map.setView(defaultCenter, 5);
      return;
    }

    const bounds = L.latLngBounds(partners.map(({ latitude, longitude }) => [latitude, longitude]));
    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.25));
    }
  }, [map, partners, autoFitEnabled]);

  return null;
}

function AnimatedCircleMarker({ position, duration = 700, ...props }) {
  const markerRef = useRef(null);
  const prevPositionRef = useRef(position);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !position) return;

    const [startLat, startLng] = prevPositionRef.current || position;
    const [endLat, endLng] = position;

    if (startLat === endLat && startLng === endLng) return;

    let start;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const lat = startLat + (endLat - startLat) * progress;
      const lng = startLng + (endLng - startLng) * progress;
      marker.setLatLng([lat, lng]);
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
    prevPositionRef.current = position;
  }, [position, duration]);

  return <CircleMarker ref={markerRef} center={position} {...props} />;
}

const getIsOnline = (partner) => partner?.is_online ?? partner?.online_status ?? false;
const normalizeUserId = (value) => {
  if (value === null || value === undefined) return undefined;
  const numericId = Number(value);
  return Number.isNaN(numericId) ? String(value) : numericId;
};
const calculateHeading = (prevLat, prevLng, nextLat, nextLng) => {
  if (
    typeof prevLat !== "number" ||
    typeof prevLng !== "number" ||
    typeof nextLat !== "number" ||
    typeof nextLng !== "number" ||
    (prevLat === nextLat && prevLng === nextLng)
  ) {
    return null;
  }
  const toRadians = (deg) => (deg * Math.PI) / 180;
  const toDegrees = (rad) => (rad * 180) / Math.PI;

  const phi1 = toRadians(prevLat);
  const phi2 = toRadians(nextLat);
  const deltaLon = toRadians(nextLng - prevLng);

  const y = Math.sin(deltaLon) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLon);
  const bearing = (toDegrees(Math.atan2(y, x)) + 360) % 360;
  return bearing;
};

function LivePartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
  const [autoFitEnabled, setAutoFitEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState("online");
  const [followedPartnerId, setFollowedPartnerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const mapRef = useRef(null);

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const hasValidCoordinates = useCallback(
    (partner) =>
      typeof partner?.latitude === "number" &&
      typeof partner?.longitude === "number" &&
      Number.isFinite(partner.latitude) &&
      Number.isFinite(partner.longitude),
    []
  );

  const normalizePartner = useCallback(
    (raw) => {
      if (!raw) return raw;

      const payload =
        raw.partner ||
        raw.driver ||
        raw.data ||
        raw.payload ||
        (Array.isArray(raw) ? raw : null) ||
        raw;

      const latitudeSource =
        payload.latitude ??
        payload.lat ??
        payload.Latitude ??
        payload.coords?.latitude ??
        payload.coords?.lat ??
        payload.location?.lat;
      const longitudeSource =
        payload.longitude ??
        payload.lng ??
        payload.lon ??
        payload.Longitude ??
        payload.coords?.longitude ??
        payload.coords?.lng ??
        payload.location?.lng;

      const latitude = typeof latitudeSource === "string" ? parseFloat(latitudeSource) : latitudeSource;
      const longitude = typeof longitudeSource === "string" ? parseFloat(longitudeSource) : longitudeSource;

      const userId = normalizeUserId(
        payload.userId ??
          payload.id ??
          payload.user_id ??
          payload.partner_id ??
          payload.partnerId ??
          payload.partnerid ??
          payload.driver_id
      );

      return {
        ...payload,
        userId,
        latitude,
        longitude,
        online_status: payload.is_online ?? payload.online_status,
        updatedAt:
          payload.updatedAt ||
          payload.updated_at ||
          payload.timestamp ||
          payload.lastUpdated ||
          payload.updated ||
          new Date().toISOString(),
      };
    },
    []
  );

  const mergePartner = useCallback(
    (incoming) => {
      const normalized = normalizePartner(incoming);
      if (normalized.userId === undefined) return;
      if (!hasValidCoordinates(normalized)) {
        setPartners((prev) => {
          const idx = prev.findIndex((partner) => partner.userId === normalized.userId);
          if (idx === -1) return prev;
          const next = [...prev];
          const prevPartner = next[idx];
          next[idx] = {
            ...prevPartner,
            ...normalized,
            latitude: prevPartner.latitude,
            longitude: prevPartner.longitude,
          };
          return next;
        });
        return;
      }

      setPartners((prev) => {
        const idx = prev.findIndex((partner) => partner.userId === normalized.userId);
        if (idx === -1) {
          return [
            ...prev,
            {
              ...normalized,
              heading: normalized.heading ?? 0,
              path: [[normalized.latitude, normalized.longitude]],
            },
          ];
        }
        const next = [...prev];
        const prevPartner = next[idx];
        const hasMoved =
          prevPartner.latitude !== normalized.latitude || prevPartner.longitude !== normalized.longitude;
        const incomingTs = toTimestamp(normalized.updatedAt);
        const existingTs = toTimestamp(prevPartner.updatedAt);
        if (!hasMoved && incomingTs > 0 && incomingTs < existingTs) {
          return prev;
        }
        const heading =
          normalized.heading ??
          calculateHeading(prevPartner.latitude, prevPartner.longitude, normalized.latitude, normalized.longitude) ??
          prevPartner.heading ??
          0;
        let nextPath = prevPartner.path || [[prevPartner.latitude, prevPartner.longitude]];
        if (hasMoved) {
          nextPath = [...nextPath, [normalized.latitude, normalized.longitude]];
          if (nextPath.length > MAX_PATH_POINTS) {
            nextPath = nextPath.slice(nextPath.length - MAX_PATH_POINTS);
          }
        }

        next[idx] = { ...prevPartner, ...normalized, heading, path: nextPath };
        return next;
      });
    },
    [hasValidCoordinates, normalizePartner]
  );

  const loadPartners = useCallback(async (withSpinner = true) => {
    if (withSpinner) {
      setLoading(true);
    }
    try {
      const response = await fetchLivePartners();
      if (response.success) {
        const normalizedPartners = (response.data || [])
          .map((partner) => normalizePartner(partner))
          .filter((partner) => hasValidCoordinates(partner) && partner.userId !== undefined);

        const deduped = [];
        const indexById = new Map();

        normalizedPartners.forEach((partner) => {
          if (indexById.has(partner.userId)) {
            const idx = indexById.get(partner.userId);
            deduped[idx] = { ...deduped[idx], ...partner };
          } else {
            indexById.set(partner.userId, deduped.length);
            deduped.push({ ...partner });
          }
        });

        setPartners((prev) => {
          const nextMap = new Map();
          prev.forEach((partner) => {
            nextMap.set(partner.userId, { ...partner, path: (partner.path || []).slice(-MAX_PATH_POINTS) });
          });

          deduped.forEach((partner) => {
            const existing = nextMap.get(partner.userId);
            if (!existing) {
              nextMap.set(partner.userId, {
                ...partner,
                heading: partner.heading ?? 0,
                path: [[partner.latitude, partner.longitude]],
              });
              return;
            }

            const incomingTs = toTimestamp(partner.updatedAt);
            const existingTs = toTimestamp(existing.updatedAt);
            const shouldUpdate =
              incomingTs > 0 ? incomingTs > existingTs : existingTs === 0;
            if (!shouldUpdate) {
              nextMap.set(partner.userId, existing);
              return;
            }

                const hasMoved =
                existing.latitude !== partner.latitude || existing.longitude !== partner.longitude;
            let updatedPath = existing.path || [[existing.latitude, existing.longitude]];
            if (hasMoved) {
              updatedPath = [...updatedPath, [partner.latitude, partner.longitude]];
              if (updatedPath.length > MAX_PATH_POINTS) {
                updatedPath = updatedPath.slice(updatedPath.length - MAX_PATH_POINTS);
              }
            }

            nextMap.set(partner.userId, {
              ...existing,
              ...partner,
              heading: partner.heading ?? existing.heading ?? 0,
                  path: updatedPath,
            });
          });

          return Array.from(nextMap.values());
        });
      }
    } catch (error) {
      showSnackbar("Failed to load live partners", "error");
    } finally {
      if (withSpinner) {
        setLoading(false);
      }
    }
  }, [hasValidCoordinates, normalizePartner, showSnackbar]);

  const handleManualRefresh = useCallback(() => {
    setAutoFitEnabled(true);
    setFollowedPartnerId(null);
    loadPartners(true);
  }, [loadPartners]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem("cdone_live_partners_visited");
    window.sessionStorage.setItem("cdone_live_partners_visited", "true");
    // Always clear cached data when navigating to this page to force a fresh fetch.
    window.localStorage.removeItem(PARTNERS_CACHE_KEY);
  }, []);

  useEffect(() => {
    setAutoFitEnabled(true);
    loadPartners(true);
  }, [loadPartners]);

  useEffect(() => {
    connectSocket();

    const handlePartnerMovement = (payload) => {
      let data = payload;

      if (typeof payload === "string") {
        try {
          data = JSON.parse(payload);
        } catch (err) {
          console.warn("Unable to parse socket payload", err);
        }
      }

      const candidates = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : data?.data
            ? [data.data]
            : [data];

      candidates.forEach((candidate) => mergePartner(candidate));
    };

    const events = ["partnerMoved", "updateLocation"];
    events.forEach((event) => socket.on(event, handlePartnerMovement));

    return () => {
      events.forEach((event) => socket.off(event, handlePartnerMovement));
      disconnectSocket();
    };
  }, [mergePartner]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!partners.length) {
      window.localStorage.removeItem(PARTNERS_CACHE_KEY);
      return;
    }
    try {
      const snapshot = partners.map((partner) => ({
        ...partner,
        path: (partner.path || []).slice(-MAX_PATH_POINTS),
      }));
      window.localStorage.setItem(PARTNERS_CACHE_KEY, JSON.stringify(snapshot));
    } catch (err) {
      console.warn("Failed to cache partners", err);
    }
  }, [partners]);

  const averagedCenter = useMemo(() => {
    if (!partners.length) return defaultCenter;
    const lat = partners.reduce((sum, partner) => sum + partner.latitude, 0) / partners.length;
    const lng = partners.reduce((sum, partner) => sum + partner.longitude, 0) / partners.length;
    return { lat, lng };
  }, [partners]);

  const { onlinePartners, offlinePartners, busyPartners } = useMemo(() => {
    const online = [];
    const offline = [];
    const busy = [];
    partners.forEach((partner) => {
      const isOnline = getIsOnline(partner);
      const hasOrder = Boolean(partner.order);
      if (isOnline && hasOrder) {
        busy.push(partner);
      } else if (isOnline && !hasOrder) {
        online.push(partner);
      } else {
        offline.push(partner);
      }
    });
    return { onlinePartners: online, offlinePartners: offline, busyPartners: busy };
  }, [partners]);

  const handlePartnerFocus = useCallback(
    (partner) => {
      if (!partner || !hasValidCoordinates(partner)) return;
      setFollowedPartnerId(partner.userId);
      setAutoFitEnabled(false);
      mapRef.current?.flyTo([partner.latitude, partner.longitude], 15, { duration: 1.25 });
    },
    [hasValidCoordinates]
  );

  const currentPartners =
    activeTab === "online" ? onlinePartners : activeTab === "offline" ? offlinePartners : busyPartners;
  const { filteredOnline, filteredOffline, filteredBusy, filteredPartners, filteredAll } = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const matchesQuery = (partner) => {
      if (!query) return true;
      const name = partner.full_name?.toLowerCase() ?? "";
      const contact = partner.order?.user?.contact?.toLowerCase() ?? "";
      const orderRef =
        (partner.order?.order_ref ?? partner.order?.orderRef ?? partner.order?.id ?? "").toString().toLowerCase();
      return name.includes(query) || contact.includes(query) || orderRef.includes(query);
    };
    const filteredOnlineNext = onlinePartners.filter(matchesQuery);
    const filteredOfflineNext = offlinePartners.filter(matchesQuery);
    const filteredBusyNext = busyPartners.filter(matchesQuery);
    const filteredCurrent =
      activeTab === "online"
        ? filteredOnlineNext
        : activeTab === "offline"
          ? filteredOfflineNext
          : filteredBusyNext;
    const filteredAllNext = [...filteredOnlineNext, ...filteredOfflineNext, ...filteredBusyNext];
    return {
      filteredOnline: filteredOnlineNext,
      filteredOffline: filteredOfflineNext,
      filteredBusy: filteredBusyNext,
      filteredPartners: filteredCurrent,
      filteredAll: filteredAllNext,
    };
  }, [activeTab, onlinePartners, offlinePartners, busyPartners, searchTerm]);
  const mapPartners = searchTerm.trim() ? filteredAll : partners;

  useEffect(() => {
    if (!followedPartnerId) return;
    const partner = partners.find((p) => p.userId === followedPartnerId);
    if (!partner) {
      setFollowedPartnerId(null);
      setAutoFitEnabled(true);
      return;
    }
    if (!hasValidCoordinates(partner)) return;
    mapRef.current?.flyTo([partner.latitude, partner.longitude], 15, { duration: 0.75 });
  }, [partners, followedPartnerId, hasValidCoordinates]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" color="white">
                    Live Partners
                  </MDTypography>
                  <Tooltip title="Refresh">
                    <IconButton onClick={handleManualRefresh} color="inherit">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <MapContainer
                      center={averagedCenter}
                      zoom={partners.length ? 10 : 5}
                      style={{ height: 500, width: "100%" }}
                      scrollWheelZoom
                      whenCreated={(mapInstance) => {
                        mapRef.current = mapInstance;
                      }}
                    >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                      <MapAutoFit
                        partners={mapPartners}
                        autoFitEnabled={autoFitEnabled}
                        onUserInteraction={() => setAutoFitEnabled(false)}
                      />
                  {mapPartners.map((partner) => {
                    const isOnline = getIsOnline(partner);
                    const hasOrder = Boolean(partner.order);
                    const color = hasOrder ? "#f97316" : isOnline ? "#16a34a" : "#dc2626";
                    const orderRef = partner.order?.order_ref ?? partner.order?.orderRef ?? partner.order?.id;
                    const orderUser = partner.order?.user;
                    return (
                      <Fragment key={partner.userId}>
                        <AnimatedCircleMarker
                          position={[partner.latitude, partner.longitude]}
                          radius={12}
                          pathOptions={circleStyles(color)}
                        >
                          <Popup maxWidth={240} minWidth={200} closeOnClick autoClose>
                            <MDBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.25 }}>
                                Partner #{partner.full_name || partner.userId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Status: {hasOrder ? "On Trip" : isOnline ? "Online" : "Offline"}
                              </Typography>
                              {hasOrder && (
                                <>
                                  <Typography variant="caption" color="text.secondary">
                                    Order: #{orderRef ?? "-"}
                                  </Typography>
                                  {orderUser && (
                                    <Typography variant="caption" color="text.secondary">
                                      Customer: {orderUser.full_name ?? "-"}{" "}
                                      {orderUser.contact ? `(${orderUser.contact})` : ""}
                                    </Typography>
                                  )}
                                </>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Last Update: {partner.updatedAt ? new Date(partner.updatedAt).toLocaleString() : "-"}
                              </Typography>
                            </MDBox>
                          </Popup>
                        </AnimatedCircleMarker>
                      </Fragment>
                    );
                  })}
                    </MapContainer>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: 500, display: "flex", flexDirection: "column" }}>
                      <Box borderBottom="1px solid rgba(0,0,0,0.12)">
                        <Tabs
                          value={activeTab}
                          onChange={(_, value) => setActiveTab(value)}
                          variant="fullWidth"
                          indicatorColor="primary"
                          textColor="primary"
                        >
                          <Tab
                            label={`Online (${searchTerm.trim() ? filteredOnline.length : onlinePartners.length})`}
                            value="online"
                          />
                          <Tab
                            label={`Offline (${searchTerm.trim() ? filteredOffline.length : offlinePartners.length})`}
                            value="offline"
                          />
                          <Tab
                            label={`Busy (${searchTerm.trim() ? filteredBusy.length : busyPartners.length})`}
                            value="busy"
                          />
                        </Tabs>
                      </Box>
                      <MDBox p={2} sx={{ flex: 1, overflowY: "auto" }}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          placeholder="Search by name, contact, or order ref"
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                          sx={{ mb: 2 }}
                        />
                        {!currentPartners.length && !searchTerm.trim() && (
                          <Typography variant="body2" color="text.secondary">
                            No partners in this state.
                          </Typography>
                        )}
                        {!!currentPartners.length && !filteredPartners.length && (
                          <Typography variant="body2" color="text.secondary">
                            No matching partners.
                          </Typography>
                        )}
                        {filteredPartners.map((partner) => {
                          const isOnline = getIsOnline(partner);
                          const hasOrder = Boolean(partner.order);
                          const orderRef = partner.order?.order_ref ?? partner.order?.orderRef ?? partner.order?.id;
                          const orderUser = partner.order?.user;
                          return (
                            <MDBox
                              key={`tab-${partner.userId}`}
                              mb={1.5}
                              p={1.5}
                              borderRadius={1}
                              border="1px solid rgba(0,0,0,0.08)"
                              sx={{ cursor: "pointer", transition: "background-color 0.2s" }}
                              onClick={() => handlePartnerFocus(partner)}
                            >
                              <MDBox sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                <Typography variant="subtitle2">
                                  {partner.full_name ? partner.full_name : `Partner #${partner.userId}`}
                                </Typography>
                                {hasOrder && orderUser && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Customer: {orderUser.full_name ?? "-"}{" "}
                                    {orderUser.contact ? `(${orderUser.contact})` : ""}
                                  </Typography>
                                )}
                                <Typography
                                  variant="button"
                                  color={hasOrder ? "warning" : isOnline ? "success" : "error"}
                                >
                                  {hasOrder ? `On Trip (${orderRef ?? "-"})` : isOnline ? "Online" : "Offline"}
                                </Typography>
                              </MDBox>
                            </MDBox>
                          );
                        })}
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <PageLoader open={loading} />
      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={closeSnackbar} />
    </DashboardLayout>
  );
}

export default LivePartners;
